import {
  MessageFormat,
  AddressFormat,
  CurrencyFormat,
  NumberFormat,
  DateTimeFormat
} from '@paypalcorp/worldready';
import type { Address } from '../typings/Common';
import { PaymentButtonType } from '../components/buttons/types';
import temporaryLocale from './temporaryLocale';

export interface WorldReadyProps {
  language: string; // en, fr
  region: string; // US, GB
  locale: string; // en-US, en-GB
  bundle: string; // en-US, en-GB
  globalize: object;
}

export type CurrencyConfigType = {
  symbol: string;
  currencyCode: string;
  maxDecimalPlaces: number;
};

export const getLocalizedString = (
  worldReady: WorldReadyProps,
  id: string | undefined,
  format: Record<string, unknown> = {}
): string => {
  if (!id) {
    return '';
  }

  const messageFormatter = new MessageFormat(worldReady, {
    id
  });
  return messageFormatter.format(format);
};

/**
 * This function is for future temporary use in early stage when content are still to be decided.
 * @param id
 * @param format
 */
export const getTempLocalizedString = (
  id: string | undefined,
  format: Record<string, string> = {}
): string => {
  if (!id) {
    return '';
  }
  const placeholderRegex = /\{(\w+?)}/g;

  /* istanbul ignore next (since we do not have a regex temp content for now) */
  return (temporaryLocale[id] || '').replace(placeholderRegex, (_, name) => format[name]);
};

interface ButtonAndLinkId {
  button: string;
  link: string;
}
export const flexButtonAndLinkContent = (type: PaymentButtonType, id: ButtonAndLinkId): string => {
  if (type === PaymentButtonType.Link) {
    return id.link;
  }
  return id.button;
};

export const getLocalizedAddress = (
  worldReady: WorldReadyProps,
  address: Address | undefined
): string => {
  if (!address) {
    return '';
  }

  const addressFormatter = new AddressFormat(worldReady, {
    addressRegion: address.country_code,
    style: 'international-multiline'
  });
  return addressFormatter.format({
    addressLine1: address.address_line_1,
    adminArea1: address.admin_area_1,
    adminArea2: address.admin_area_2,
    countryCode: address.country_code,
    postalCode: address.postal_code
  });
};

export const getLocalizedAddressParts = (
  worldReady: WorldReadyProps,
  address: Address | undefined
): ReturnType<AddressFormat['formatToParts']> => {
  if (!address) {
    return [];
  }

  const addressFormatter = new AddressFormat(worldReady, {
    addressRegion: address.country_code,
    style: 'international-multiline'
  });
  return addressFormatter.formatToParts({
    addressLine1: address.address_line_1,
    adminArea1: address.admin_area_1,
    adminArea2: address.admin_area_2,
    countryCode: address.country_code,
    postalCode: address.postal_code
  });
};

export const getCurrencyConfig = (
  worldReady: WorldReadyProps,
  currency: string | undefined
): CurrencyConfigType => {
  if (!currency) {
    return {
      symbol: '$',
      currencyCode: 'USD',
      maxDecimalPlaces: 2
    };
  }

  const currencyFormatter = new CurrencyFormat(worldReady, {
    currency,
    style: 'international'
  });
  const maxDecimalPlaces = currencyFormatter.resolvedOptions().maximumFractionDigits;
  const dummyParts = currencyFormatter.formatToParts(1);
  const symbol = dummyParts.find((part) => part.type === 'currencySymbol')?.value;
  const currencyCode = dummyParts.find((part) => part.type === 'currencyCode')?.value;

  return {
    symbol: symbol || '',
    currencyCode: currencyCode || currency,
    maxDecimalPlaces
  };
};

export const formatCurrency = (
  worldReady: WorldReadyProps,
  currencyCode: string | undefined,
  currencyAmount: string | number | undefined,
  showCurrencySymbol: boolean = false,
  showCurrencyCode: boolean = true
) => {
  if (currencyCode && currencyAmount != null) {
    const currencyOptions = showCurrencyCode
      ? { currency: currencyCode, style: 'international' }
      : { currency: currencyCode };
    const currencyFormatter = new CurrencyFormat(worldReady, currencyOptions);

    if (showCurrencySymbol) {
      return currencyFormatter.format(Number(currencyAmount));
    }
    const { minimumFractionDigits, maximumFractionDigits } = currencyFormatter.resolvedOptions();

    const numberFormatter = new NumberFormat(worldReady, {
      minimumFractionDigits,
      maximumFractionDigits
    });
    const formattedCurrencyAmount = numberFormatter.format(Number(currencyAmount));

    return `${formattedCurrencyAmount} ${currencyCode}`;
  }
  return null;
};

export const getLocalizedDate = (
  worldReady: WorldReadyProps,
  timeZone: string,
  date: Date,
  options?: {
    style?: string;
  }
) => {
  if (!date) {
    return '';
  }

  const dateTimeFormatter = new DateTimeFormat(worldReady, {
    style: options?.style || 'date-short',
    timeZone
  });
  return dateTimeFormatter.format(date);
};

export const getLocalizedPercentage = (
  worldReady: WorldReadyProps,
  number: number | string | undefined
) => {
  if (!number) {
    return '';
  }

  const numberFormatter = new NumberFormat(worldReady, {
    style: 'percent',
    maximumFractionDigits: 4
  });

  return numberFormatter.format(Number(number) / 100);
};
